import { useEffect, useRef } from 'react';
import type { GridColumnsManagementProps } from '@mui/x-data-grid';
import {
  GridColumnsManagement,
  gridColumnsStateSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';

interface ColumnsManagementResetWrapperProps
  extends GridColumnsManagementProps {
  onReset?: () => void;
}

export const ColumnsManagementResetWrapper = ({
  onReset,
  ...props
}: ColumnsManagementResetWrapperProps) => {
  const apiRef = useGridApiContext();
  const columnsState = useGridSelector(apiRef, gridColumnsStateSelector);
  const initialColumnVisibilityModel =
    columnsState.initialColumnVisibilityModel;
  const onResetRef = useRef(onReset);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    onResetRef.current = onReset;
  }, [onReset]);

  useEffect(() => {
    const findResetButton = () => {
      const container = containerRef.current;
      if (!container) return;

      const footer = container.querySelector(
        '.MuiDataGrid-columnsManagementFooter',
      ) as HTMLElement;
      if (!footer) return;

      const resetText = apiRef.current.getLocaleText('columnsManagementReset');
      const buttons = Array.from(footer.querySelectorAll('button'));

      buttons.forEach((button) => {
        const buttonText = button.textContent?.trim();
        if (buttonText === resetText && !button.dataset.resetHandled) {
          button.dataset.resetHandled = 'true';

          button.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            console.log('Reset button clicked');
            if (onResetRef.current) {
              console.log('Calling resetState');
              onResetRef.current();
            }

            setTimeout(() => {
              apiRef.current.restoreState({
                columns: {
                  columnVisibilityModel: initialColumnVisibilityModel,
                },
                sorting: {},
                filter: {},
              });
            }, 100);
          };
        }
      });
    };

    const observer = new MutationObserver(findResetButton);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });
      findResetButton();
    }

    return () => {
      observer.disconnect();
    };
  }, [apiRef, initialColumnVisibilityModel]);

  return (
    <div ref={containerRef}>
      <GridColumnsManagement {...props} />
    </div>
  );
};
